import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { BrandLockup } from "./BrandLockup";
import { printScoped } from "../lib/printScope";
import {
  printTitle, rowRest, rowValue, setsWord, supersetSets,
  type PrintBlock, type PrintRow, type WorkoutPrintout,
} from "../lib/workoutPrintout";

/* 🔴 QUEUE N2b-2 — THE PRINTED WORKOUT, AS EVERFIT'S GRID (their print: `docs/reference/everfit-print-workout-20261006.pdf`).
 * The phone's `WorkoutPrintRenderer` draws the same `WorkoutPrintout` in Core Graphics; this draws it in HTML for the browser's
 * own print path (copies, range, Save as PDF). Each workout is one TABLE whose `thead` is the header — the browser repeats a
 * table's head on every page it spans, which is Everfit's top line on page 2 — and starts on a new page. The boxes, the band,
 * the tab, the cells and their order are the phone's; `pro.css`'s `@media print` block (LAST in the sheet) draws them. */

export function WorkoutPrintSheet({ printouts }: { printouts: readonly WorkoutPrintout[] }) {
  return (
    <div className="pw-sheet">
      {printouts.map((p, i) => (
        <table key={i} className="pw-workout">
          <thead>
            <tr><td>
              <header className="pw-head">
                <span className="pw-logo"><BrandLockup size={13} /></span>
                <h1 className="pw-title">{p.title}</h1>
                <p className="pw-who">
                  {p.client !== "" && <b>{p.client}</b>}
                  {p.date !== "" && <span>{p.date}</span>}
                </p>
              </header>
            </td></tr>
          </thead>
          <tbody>
            <tr><td>
              {(p.description !== "" || p.equipment.length > 0) && (
                <div className="pw-intro">
                  {p.description !== "" && <p className="pw-desc">{p.description}</p>}
                  {p.equipment.length > 0 && <p className="pw-kit"><b>Equipment:</b> {p.equipment.join(", ")}</p>}
                </div>
              )}
              {p.blocks.map((b, bi) => <Block key={bi} block={b} />)}
            </td></tr>
          </tbody>
        </table>
      ))}
    </div>
  );
}

function Block({ block: b }: { block: PrintBlock }) {
  let lastRun: number | null = null;
  return (
    <section className={`pw-block is-${b.kind}`}>
      {b.kind === "superset" && (
        <p className="pw-tab"><b>SUPERSET</b> — {setsWord(supersetSets(b, null))}</p>
      )}
      <div className="pw-box">
        {b.kind === "section" && (
          <div className="pw-band">
            <p className="pw-band-title">
              {b.title !== "" ? <>{b.title}<span className="pw-band-format"> — {b.format}</span></> : b.format}
            </p>
            {b.figure !== "" && <p className="pw-band-figure">{b.figure}</p>}
            {b.note !== "" && <p className="pw-band-note">{b.note}</p>}
          </div>
        )}
        {b.rows.map((r, ri) => {
          const strip = b.kind === "section" && r.superset !== null && r.superset !== lastRun;
          lastRun = r.superset;
          return (
            <div key={ri} className="pw-rowwrap">
              {strip && <p className="pw-strip"><b>SUPERSET</b> — {setsWord(supersetSets(b, r.superset))}</p>}
              <Row row={r} timed={b.timed} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Four cells to a line — the phone's (`WorkoutPrintRenderer.perLine` at A4); `pro.css`'s `.pw-cells` repeats it. */
const CELLS_PER_LINE = 4;

function Row({ row: r, timed }: { row: PrintRow; timed: boolean }) {
  const name = (
    <p className="pw-name">{r.number !== "" && <span className="pw-num">{r.number} </span>}<b>{r.name}</b></p>
  );
  if (timed) {
    return (
      <div className="pw-row is-timed">
        {name}
        <div className="pw-value"><b>{rowValue(r)}</b>{r.note !== "" && <p className="pw-note">{r.note}</p>}</div>
        <p className="pw-rest pw-rest-col"><span>REST</span><span>{rowRest(r)}</span></p>
      </div>
    );
  }
  return (
    <div className="pw-row">
      {name}
      <div className="pw-right">
        {r.cells.length > 0 && (
          <div className="pw-cells">
            {r.cells.map((c, ci) => (
              <div key={ci} className="pw-cell">
                <p className="pw-set">
                  <span className="pw-label">{/^\d+$/.test(c.label) ? `${c.label}.` : c.label}</span><b>{c.text}</b>
                </p>
                <p className="pw-rest"><span>REST</span><span>{c.rest}</span></p>
              </div>
            ))}
            {/* The last line is filled out with empty cells so its two rules run to the box's edge, as theirs do. */}
            {Array.from({ length: (CELLS_PER_LINE - (r.cells.length % CELLS_PER_LINE)) % CELLS_PER_LINE }, (_, k) => (
              <div key={`pad${String(k)}`} className="pw-cell is-pad" aria-hidden="true">
                <p className="pw-set" /><p className="pw-rest" />
              </div>
            ))}
          </div>
        )}
        {r.note !== "" && <p className="pw-note">{r.note}</p>}
      </div>
    </div>
  );
}

/** Mounts the sheet beside the app, prints it, and takes it away after the dialog (printed or cancelled). The document's
 *  title is the browser's suggested PDF name, so it is the workout's while the dialog is open. */
export function printWorkouts(printouts: readonly WorkoutPrintout[]): void {
  if (printouts.length === 0) return;
  printScoped(() => {
    const host = document.createElement("div");
    host.className = "print-root";
    document.body.appendChild(host);
    const root = createRoot(host);
    flushSync(() => { root.render(<WorkoutPrintSheet printouts={printouts} />); });
    const title = document.title;
    document.title = printTitle(printouts);
    return () => {
      document.title = title;
      root.unmount();
      host.remove();
    };
  });
}
